import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, FileText, Users, Building2, DollarSign, BarChart3, ChartCandlestick, BookLock, BanknoteArrowUp, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';

const Servicios = () => {
  const lineaNegocio1 = {
    title: "CFO Externo y Consultoría Financiera",
    description: "Dirección financiera estratégica para empresas que necesitan experticia de alto nivel sin los costos de un CFO interno.",
    services: [
      {
        icon: <TrendingUp className="w-8 h-8" />,
        name: "CFO externo para PYMEs",
        description: "Liderazgo financiero estratégico con visión integral del negocio"
      },
      {
        icon: <FileText className="w-8 h-8" />,
        name: "Diagnóstico financiero estratégico",
        description: "Análisis profundo de la situación financiera y oportunidades de mejora"
      },
      {
        icon: <TrendingUp className="w-8 h-8" />,
        name: "Metodología de aceleración del valor",
        description: "Estrategias para aumentar el valor de tu empresa de forma sistemática"
      },
      {
        icon: <BarChart3 className="w-8 h-8" />,
        name: "Flujo de caja y proyecciones",
        description: "Control de liquidez y planificación financiera a mediano plazo"
      },
      {
        icon: <ChartCandlestick className="w-8 h-8" />,
        name: "Análisis de pricing y rentabilidad",
        description: "Optimización de márgenes y estructura de precios"
      },
      {
        icon: <Building2 className="w-8 h-8" />,
        name: "Estrategia de crecimiento financiero",
        description: "Planes de escalamiento y expansión sostenible"
      },
      {
        icon: <Users className="w-8 h-8" />,
        name: "Transformación cultural empresarial",
        description: "Desarrollo de cultura financiera en equipos de liderazgo"
      },
      {
        icon: <TrendingUp className="w-8 h-8" />,
        name: "Mentoring a fundadores y gerentes",
        description: "Acompañamiento personalizado para toma de decisiones estratégicas"
      },

    ]
  };

  const lineaNegocio2 = {
    title: "Centro de Servicios Compartidos Contables",
    description: "Operación contable y administrativa completa para que te enfoques en hacer crecer tu negocio.",
    services: [
      {
        icon: <BookLock className="w-8 h-8" />,
        name: "Contabilidad financiera",
        description: "Registro contable bajo NIIF para PYMEs con reportes mensuales"
      },
      {
        icon: <FileText className="w-8 h-8" />,
        name: "Facturación electrónica",
        description: "Emisión y gestión de facturas electrónicas según normativa DIAN"
      },
      {
        icon: <Users className="w-8 h-8" />,
        name: "Nómina electrónica y seguridad social",
        description: "Liquidación de nómina, prestaciones sociales y aportes"
      },
      {
        icon: <BanknoteArrowUp className="w-8 h-8" />,
        name: "Tesorería",
        description: "Control de ingresos, egresos y conciliaciones bancarias"
      },
      {
        icon: <Handshake className="w-8 h-8" />,
        name: "Gestión de cartera y proveedores",
        description: "Seguimiento de cuentas por cobrar y pagar"
      },
      {
        icon: <FileText className="w-8 h-8" />,
        name: "Reportes contables y administrativos",
        description: "Estados financieros e informes gerenciales mensuales"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        {/* Meta Tags Básicos */}
        <title>Servicios de CFO Externo y Consultoría Financiera | Sinergia Empresarial CSCE</title>
        <meta name="description" content="CFO externo, consultoría financiera, diagnóstico estratégico, flujo de caja, contabilidad NIIF, nómina electrónica y facturación para PYMEs en Colombia." />
        <meta name="keywords" content="CFO externo Colombia, consultoría financiera, diagnóstico financiero, flujo de caja, contabilidad NIIF, nómina electrónica, facturación electrónica DIAN" />
        <link rel="canonical" href="https://www.sinergiaempresarialcsce.com/servicios" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.sinergiaempresarialcsce.com/servicios" />
        <meta property="og:title" content="Servicios de CFO Externo y Consultoría Financiera" />
        <meta property="og:description" content="CFO externo, consultoría financiera, diagnóstico estratégico, contabilidad NIIF y servicios administrativos para PYMEs." />
        <meta property="og:image" content="https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://www.sinergiaempresarialcsce.com/servicios" />
        <meta name="twitter:title" content="Servicios de CFO Externo y Consultoría Financiera" />
        <meta name="twitter:description" content="CFO externo, consultoría financiera, diagnóstico estratégico y servicios contables para PYMEs." />
        <meta name="twitter:image" content="https://www.sinergiaempresarialcsce.com/logotipo_sinergia.png" />
        
        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Consultoría Financiera y Servicios Contables",
            "provider": {
              "@type": "Organization",
              "name": "Sinergia Empresarial CSCE",
              "url": "https://www.sinergiaempresarialcsce.com"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Colombia"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Servicios Financieros y Contables",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "CFO Externo",
                    "description": "Dirección financiera estratégica para PYMEs"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Consultoría Financiera",
                    "description": "Diagnóstico financiero y estrategia de crecimiento"
                  }
                },
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Servicios Contables",
                    "description": "Contabilidad NIIF, nómina y facturación electrónica"
                  }
                }
              ]
            }
          })}
        </script>
      </Helmet>

      {/* Header Section */}
      <section className="bg-gradient-to-r from-sinergia-azul to-sinergia-verde text-white py-16">
        <div className="container mx-auto px-6">
          <h1 className="heading-1 text-5xl font-bold mb-4">
            Nuestros Servicios
          </h1>
          <p className="body-large max-w-3xl">
            Soluciones financieras y contables diseñadas específicamente para pequeñas y medianas empresas que buscan crecer con control y rentabilidad.
          </p>
        </div>
      </section>

      {/* Línea de Negocio 1 */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="mb-12">
            <h2 className="heading-2 text-4xl font-bold mb-4 text-sinergia-gris-oscuro">
              {lineaNegocio1.title}
            </h2>
            <p className="body-medium text-gray-600 max-w-3xl">
              {lineaNegocio1.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lineaNegocio1.services.map((service, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="text-sinergia-azul mb-4">
                  {service.icon}
                </div>
                <h3 className="heading-5 text-lg font-semibold mb-2 text-sinergia-gris-oscuro">
                  {service.name}
                </h3>
                <p className="body-small text-gray-600">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/paquetes-precios">
              <Button className="btn-primary px-8 py-4">
                Ver paquetes y precios
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Línea de Negocio 2 */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="mb-12">
            <h2 className="heading-2 text-4xl font-bold mb-4 text-sinergia-gris-oscuro">
              {lineaNegocio2.title}
            </h2>
            <p className="body-medium text-gray-600 max-w-3xl">
              {lineaNegocio2.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lineaNegocio2.services.map((service, index) => (
              <div 
                key={index}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="text-sinergia-verde mb-4">
                  {service.icon}
                </div>
                <h3 className="heading-5 text-lg font-semibold mb-2 text-sinergia-gris-oscuro">
                  {service.name}
                </h3>
                <p className="body-small text-gray-600">
                  {service.description}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/paquetes-precios">
              <Button className="btn-primary px-8 py-4">
                Ver paquetes y precios
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-sinergia-azul text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="heading-2 text-4xl font-bold mb-4">
            ¿No estás seguro qué servicio necesitas?
          </h2>
          <p className="body-large mb-8 max-w-2xl mx-auto">
            Agenda una consulta y te ayudaremos a identificar la mejor solución para tu empresa.
          </p>
          <Link to="/contacto">
            <Button className="bg-white text-sinergia-azul hover:bg-gray-100 px-8 py-4 text-lg font-semibold rounded-full">
              Agenda tu consulta gratuita
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Servicios;
